// ============================================================
// Shared mode + supabase resolver for the data hooks.
//
// LIVE iff:
//   - VITE_OLIVIUM_MODE === 'live'
//   - VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY are set
//   - tryCreateBrowserClient() returns a non-null client
//
// In every other case the hooks fall back to honest demo data.
// Env is read via loadFrontendEnv() which resolves to whatever
// the Vite app injected through setFrontendEnv() at boot.
// ============================================================

import { loadFrontendEnv, tryCreateBrowserClient } from '@square/core'
import type { SupabaseClient } from '@supabase/supabase-js'

type Maybe<T> = T | null

let cachedClient: Maybe<SupabaseClient> = null
let resolved = false

export function getRuntime(): { live: boolean; supabase: Maybe<SupabaseClient> } {
  if (!resolved) {
    const env = loadFrontendEnv()
    const wantsLive = env.VITE_OLIVIUM_MODE === 'live'
    const hasCreds = Boolean(env.VITE_SUPABASE_URL && env.VITE_SUPABASE_ANON_KEY)
    if (wantsLive && hasCreds) {
      cachedClient = tryCreateBrowserClient()
    }
    resolved = true
  }
  return {
    live: cachedClient !== null,
    supabase: cachedClient,
  }
}

/**
 * Force a re-evaluation of mode + cached client. Useful from tests
 * and from diagnostic flows where the app swaps env after boot.
 */
export function resetRuntime(): void {
  cachedClient = null
  resolved = false
}
